// Hook for managing Supabase MCP connection
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';

interface SupabaseConnection {
    connected: boolean;
    connection: {
        type: 'oauth' | 'pat';
        project_ref: string | null;
        project_name: string | null;
        pixel_project_id?: string; // Added to support project scoping
        created_at: string;
        anon_key?: string;
        service_role_key?: string;
    } | null;
}

interface SupabaseProject {
    id: string;
    name: string;
    region: string;
    status: string;
    usedByPixelProjectId?: string | null;
}

interface MCPExecuteResult {
    success: boolean;
    data?: any;
    error?: string;
}

export function useSupabaseMCP(pixelProjectId?: string) {
    const { user, session } = useAuth();
    const [connection, setConnection] = useState<SupabaseConnection | null>(null);
    const [projects, setProjects] = useState<SupabaseProject[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Base URL for MCP proxy
    const getMCPProxyUrl = useCallback(() => {
        const baseUrl = import.meta.env.VITE_SUPABASE_URL;
        if (!baseUrl) {
            throw new Error('VITE_SUPABASE_URL not configured');
        }
        return `${baseUrl}/functions/v1/supabase-mcp-proxy`;
    }, []);

    // Get auth headers
    const getAuthHeaders = useCallback(() => {
        if (!session?.access_token) {
            throw new Error('Not authenticated');
        }
        return {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
        };
    }, [session]);

    // Fetch connection status
    const fetchStatus = useCallback(async () => {
        if (!session?.access_token) return;

        try {
            setIsLoading(true);
            const statusUrl = pixelProjectId
                ? `${getMCPProxyUrl()}/status?pixel_project_id=${pixelProjectId}`
                : `${getMCPProxyUrl()}/status`;
            const response = await fetch(statusUrl, {
                headers: getAuthHeaders(),
            });

            if (!response.ok) {
                throw new Error('Failed to fetch status');
            }

            const data = await response.json();
            setConnection(data);
        } catch (err: any) {
            console.error('[useSupabaseMCP] Error fetching status:', err);
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, [session, getMCPProxyUrl, getAuthHeaders, pixelProjectId]);

    // Initial fetch
    useEffect(() => {
        if (session?.access_token) {
            fetchStatus();
        }
    }, [session?.access_token, fetchStatus, pixelProjectId]);

    // Initiate OAuth flow
    const connectWithOAuth = useCallback(async () => {
        try {
            setError(null);
            const response = await fetch(`${getMCPProxyUrl()}/oauth/authorize`, {
                headers: getAuthHeaders(),
            });

            if (!response.ok) {
                throw new Error('Failed to initiate OAuth');
            }

            const { authorize_url } = await response.json();

            // Clear any previous OAuth data
            localStorage.removeItem('supabase-oauth-code');
            localStorage.removeItem('supabase-oauth-error');

            // Open popup for OAuth
            const popup = window.open(
                authorize_url,
                'supabase-oauth',
                'width=600,height=700,left=200,top=100'
            );

            // Helper to exchange code for tokens
            const exchangeCode = async (code: string) => {
                console.log('[useSupabaseMCP] Exchanging code for tokens...');
                try {
                    const redirectUri = `https://${new URL(getMCPProxyUrl()).host}/functions/v1/supabase-mcp-proxy/oauth/callback`;
                    console.log('[useSupabaseMCP] Using redirect_uri:', redirectUri);

                    const callbackResponse = await fetch(`${getMCPProxyUrl()}/oauth/callback`, {
                        method: 'POST',
                        headers: getAuthHeaders(),
                        body: JSON.stringify({
                            code,
                            redirect_uri: redirectUri,
                        }),
                    });

                    console.log('[useSupabaseMCP] Token exchange response:', callbackResponse.status);

                    if (callbackResponse.ok) {
                        console.log('[useSupabaseMCP] Token exchange SUCCESS, fetching status...');
                        await fetchStatus();
                    } else {
                        const errorData = await callbackResponse.json();
                        console.error('[useSupabaseMCP] Token exchange FAILED:', errorData);
                        setError(errorData.error || 'Token exchange failed');
                    }
                } catch (err: any) {
                    console.error('[useSupabaseMCP] Token exchange ERROR:', err);
                    setError(err.message);
                }
            };

            let codeProcessed = false;

            // Method 1: Listen for postMessage (may work if popup opener reference maintained)
            const handleMessage = async (event: MessageEvent) => {
                if (codeProcessed) return;

                if (event.data?.type === 'supabase-oauth-success' && event.data.code) {
                    codeProcessed = true;
                    window.removeEventListener('message', handleMessage);
                    popup?.close();
                    await exchangeCode(event.data.code);
                } else if (event.data?.type === 'supabase-oauth-error') {
                    codeProcessed = true;
                    window.removeEventListener('message', handleMessage);
                    popup?.close();
                    setError(event.data.error || 'OAuth failed');
                }
            };
            window.addEventListener('message', handleMessage);

            // Method 2: BroadcastChannel (modern browsers)
            let bc: BroadcastChannel | null = null;
            try {
                bc = new BroadcastChannel('supabase-oauth');
                bc.onmessage = async (event) => {
                    if (codeProcessed) return;
                    if (event.data?.type === 'success' && event.data.code) {
                        codeProcessed = true;
                        bc?.close();
                        popup?.close();
                        await exchangeCode(event.data.code);
                    }
                };
            } catch (e) {
                // BroadcastChannel not supported
            }

            // Method 3: Poll localStorage (most reliable fallback)
            const pollInterval = setInterval(async () => {
                if (codeProcessed) {
                    clearInterval(pollInterval);
                    return;
                }

                // Check for code
                const codeData = localStorage.getItem('supabase-oauth-code');
                if (codeData) {
                    try {
                        const { code, timestamp } = JSON.parse(codeData);
                        // Only process if recent (within 5 minutes)
                        if (Date.now() - timestamp < 300000 && code) {
                            codeProcessed = true;
                            clearInterval(pollInterval);
                            localStorage.removeItem('supabase-oauth-code');
                            popup?.close();
                            await exchangeCode(code);
                        }
                    } catch (e) {
                        console.error('Error parsing OAuth code:', e);
                    }
                }

                // Check for error
                const errorData = localStorage.getItem('supabase-oauth-error');
                if (errorData) {
                    try {
                        const { error, timestamp } = JSON.parse(errorData);
                        if (Date.now() - timestamp < 300000) {
                            codeProcessed = true;
                            clearInterval(pollInterval);
                            localStorage.removeItem('supabase-oauth-error');
                            popup?.close();
                            setError(error || 'OAuth failed');
                        }
                    } catch (e) {
                        console.error('Error parsing OAuth error:', e);
                    }
                }

                // Check if popup was closed manually
                if (popup?.closed) {
                    clearInterval(pollInterval);
                }
            }, 500);

            // Cleanup after timeout (5 minutes)
            setTimeout(() => {
                clearInterval(pollInterval);
                window.removeEventListener('message', handleMessage);
                bc?.close();
            }, 300000);

        } catch (err: any) {
            console.error('[useSupabaseMCP] OAuth error:', err);
            setError(err.message);
        }
    }, [getMCPProxyUrl, getAuthHeaders, fetchStatus]);

    // Connect with Personal Access Token
    const connectWithPAT = useCallback(async (accessToken: string, projectRef?: string, projectName?: string) => {
        try {
            setError(null);
            setIsLoading(true);

            const response = await fetch(`${getMCPProxyUrl()}/pat`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    access_token: accessToken,
                    project_ref: projectRef,
                    project_name: projectName,
                }),
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to connect with PAT');
            }

            // Refresh status
            await fetchStatus();
            return true;
        } catch (err: any) {
            console.error('[useSupabaseMCP] PAT error:', err);
            setError(err.message);
            return false;
        } finally {
            setIsLoading(false);
        }
    }, [getMCPProxyUrl, getAuthHeaders, fetchStatus]);

    // Disconnect
    const disconnect = useCallback(async () => {
        try {
            setError(null);
            const response = await fetch(`${getMCPProxyUrl()}/disconnect`, {
                method: 'DELETE',
                headers: getAuthHeaders(),
            });

            if (!response.ok) {
                throw new Error('Failed to disconnect');
            }

            setConnection(null);
            setProjects([]);
        } catch (err: any) {
            console.error('[useSupabaseMCP] Disconnect error:', err);
            setError(err.message);
        }
    }, [getMCPProxyUrl, getAuthHeaders]);

    // Fetch organizations
    const fetchOrganizations = useCallback(async () => {
        if (!connection?.connected) return [];

        try {
            const response = await fetch(`${getMCPProxyUrl()}/organizations`, {
                headers: getAuthHeaders(),
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to fetch organizations');
            }

            const data = await response.json();
            return data.organizations || [];
        } catch (err: any) {
            console.error('[useSupabaseMCP] Organizations error:', err);
            setError(err.message);
            return [];
        }
    }, [connection?.connected, getMCPProxyUrl, getAuthHeaders]);

    // Create a new Supabase project
    const createProject = useCallback(async (params: {
        organization_id: string;
        name: string;
        region?: string;
        plan?: 'free' | 'pro';
        db_pass?: string;
        pixel_project_id?: string;
    }) => {
        try {
            setError(null);
            const response = await fetch(`${getMCPProxyUrl()}/projects/create`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(params),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to create project');
            }

            // Refresh projects list
            await fetchStatus();
            return { success: true, project: data.project };
        } catch (err: any) {
            console.error('[useSupabaseMCP] Create project error:', err);
            setError(err.message);
            return { success: false, error: err.message };
        }
    }, [getMCPProxyUrl, getAuthHeaders, fetchStatus]);

    // Fetch projects
    const fetchProjects = useCallback(async () => {
        if (!connection?.connected) return [];

        try {
            const response = await fetch(`${getMCPProxyUrl()}/projects`, {
                headers: getAuthHeaders(),
            });

            if (!response.ok) {
                throw new Error('Failed to fetch projects');
            }

            const data = await response.json();
            setProjects(data.projects || []);
            return data.projects || [];
        } catch (err: any) {
            console.error('[useSupabaseMCP] Projects error:', err);
            setError(err.message);
            return [];
        }
    }, [connection?.connected, getMCPProxyUrl, getAuthHeaders]);

    // Select a project
    const selectProject = useCallback(async (projectRef: string, projectName: string) => {
        try {
            setError(null);
            const response = await fetch(`${getMCPProxyUrl()}/projects/select`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    project_ref: projectRef,
                    project_name: projectName,
                    pixel_project_id: pixelProjectId // Pass pixelProjectId to scope connection
                }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                console.error('[useSupabaseMCP] Select project failed:', response.status, errorData);
                throw new Error(errorData.error || `Failed to select project (${response.status})`);
            }

            // Refresh status
            await fetchStatus();
            return true;
        } catch (err: any) {
            console.error('[useSupabaseMCP] Select project error:', err);
            setError(err.message);
            return false;
        }
    }, [getMCPProxyUrl, getAuthHeaders, fetchStatus, pixelProjectId]);

    // Execute MCP action (SQL, list tables, etc)
    const executeAction = useCallback(async (
        action: 'execute_sql' | 'list_tables' | 'get_schema' | 'list_functions' | 'get_logs' | 'admin_operation' | 'upload_image' | 'deploy_edge_function' | 'manage_auth_users' | 'update_email_template',
        params?: Record<string, any>
    ): Promise<MCPExecuteResult> => {
        if (!connection?.connected || !connection.connection?.project_ref) {
            return { success: false, error: 'No Supabase project connected' };
        }

        // Strict check: if in a pixel project, ensure connection matches
        if (pixelProjectId && connection.connection.pixel_project_id && connection.connection.pixel_project_id !== pixelProjectId) {
            return { success: false, error: 'Project mismatch: Supabase connection belongs to another Pixel project' };
        }

        try {
            const response = await fetch(`${getMCPProxyUrl()}/mcp/execute`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ action, params, pixel_project_id: pixelProjectId }), // Ensure pixel_project_id is sent
            });

            const data = await response.json();

            if (!response.ok) {
                return { success: false, error: data.error || 'Action failed' };
            }

            return { success: true, data };
        } catch (err: any) {
            console.error('[useSupabaseMCP] Execute error:', err);
            return { success: false, error: err.message };
        }
    }, [connection, getMCPProxyUrl, getAuthHeaders, pixelProjectId]);

    // Convenience methods
    const executeSql = useCallback(async (sql: string) => {
        return executeAction('execute_sql', { sql });
    }, [executeAction]);

    const listTables = useCallback(async () => {
        return executeAction('list_tables');
    }, [executeAction]);

    const getSchema = useCallback(async () => {
        return executeAction('get_schema');
    }, [executeAction]);

    const listEdgeFunctions = useCallback(async () => {
        return executeAction('list_functions');
    }, [executeAction]);

    const getLogs = useCallback(async (type?: string, limit?: number) => {
        return executeAction('get_logs', { type, limit });
    }, [executeAction]);

    // Admin Action (Bypass RLS)
    const executeAdminAction = useCallback(async (
        table: string,
        operation: 'insert' | 'update' | 'delete' | 'select',
        data?: any,
        match?: Record<string, any>,
        filters?: any
    ) => {
        return executeAction('admin_operation', { table, operation, data, match, filters });
    }, [executeAction]);

    // Deploy Edge Function (Configuration only for now)
    const deployEdgeFunction = useCallback(async (params: { slug: string, name: string, verify_jwt?: boolean, import_map?: boolean, entrypoint_path?: string }) => {
        return executeAction('deploy_edge_function', params);
    }, [executeAction]);

    // Manage Auth Users
    const manageAuthUsers = useCallback(async (action: 'create' | 'delete' | 'list' | 'update', userData?: any, userId?: string) => {
        return executeAction('manage_auth_users', { action, userData, userId });
    }, [executeAction]);

    // Update Email Template (Auth Config)
    const updateEmailTemplate = useCallback(async (config: any) => {
        return executeAction('update_email_template', config);
    }, [executeAction]);

    // Strict connection check for return value
    const isStrictlyConnected = connection?.connected &&
        (!pixelProjectId || !connection.connection?.pixel_project_id || connection.connection.pixel_project_id === pixelProjectId);

    return {
        // State
        connection,
        projects,
        isLoading,
        error,
        isConnected: isStrictlyConnected, // Use strict check
        selectedProject: isStrictlyConnected ? connection?.connection?.project_ref ?? null : null,
        selectedProjectName: isStrictlyConnected ? connection?.connection?.project_name ?? null : null,
        // Expose keys if available
        anonKey: isStrictlyConnected ? connection?.connection?.anon_key ?? null : null,
        serviceRoleKey: isStrictlyConnected ? connection?.connection?.service_role_key ?? null : null,

        // Actions
        connectWithOAuth,
        connectWithPAT,
        disconnect,
        fetchOrganizations,
        fetchProjects,
        createProject,
        selectProject,
        refreshStatus: fetchStatus,

        // MCP Actions
        executeSql,
        listTables,
        getSchema,
        listEdgeFunctions,
        getLogs,
        executeAction,
        executeAdminAction,
        deployEdgeFunction,
        manageAuthUsers,
        updateEmailTemplate,
    };
}
